// MINSKY-16657 — mounts the agentic ai-assistant backend (MINSKY-16473/16479/16655/16656) into
// the real app tree. This component drives `useAgenticRunController` (agenticRun/).
// `AIAssistantChatTab.jsx`/`GlobalAiAssistantPanel.jsx` (this component's only real callers)
// needed NO changes — the prop contract below is unchanged from before the cutover.
//
// This panel renders `useAgenticRunController`'s full `history` (every PAST exchange for the
// active thread, oldest-first, via `agenticRun/components/AgenticExchangeHistory.jsx`) with the
// live/current turn (goal + live reasoning/steps + final answer) appended at the bottom and
// auto-scrolled into view (`agenticRun/hooks/useAgenticScrollToBottom.js`). Switching to a
// different thread via the history drawer (`ThreadHistory.jsx`, wired to T4's
// `GET/DELETE /conversations*`) loads that thread's FULL scrollback the same way.
import React from 'react';
import {Alert, Box, CircularProgress, Paper, Stack, Typography} from '@mui/material';
import ChatHeader from './components/ChatHeader';
import EmptyState from './components/EmptyState';
import ChartPhraseChips from './components/ChartPhraseChips';
import MessageComposer from '../../components/MessageComposer';
import {getCachedExpertByUid} from '../products/components/common/authorCache';
import useAgenticRunController, {AGENTIC_RUN_STATUS} from './agenticRun/useAgenticRunController';
import useAgenticScrollToBottom, {buildAgenticScrollMetrics} from './agenticRun/hooks/useAgenticScrollToBottom';
import AgenticStepList from './agenticRun/components/AgenticStepList';
import ReasoningSection from './agenticRun/components/ReasoningSection';
import AgenticFinalAnswer from './agenticRun/components/AgenticFinalAnswer';
import AgenticExchangeHistory from './agenticRun/components/AgenticExchangeHistory';
import ChartMutationPreview from './agenticRun/components/ChartMutationPreview';
import PureLlmMutationPreview, {
    CHART_BRIDGE_NOT_READY_COPY,
    CHART_PREVIEW_EMPTY_COPY,
    isEmptyConfirmableMarkupPreview,
    isMovesOnlyMarkup,
} from './agenticRun/components/PureLlmMutationPreview';
import ContextSuggestPreview from './agenticRun/components/ContextSuggestPreview';
import TraceIndirectConnectionsPicker from './components/TraceIndirectConnectionsPicker';
import useChartAiBridgeStore from '../../stores/useChartAiBridgeStore';
import {resolveEntityRefFromPayload} from './services/inlineComponents/decodeInlineComponentStream';
import {isTraceIndirectConnectionsIntent} from './utils/disjointChartEntities';
import {collectChartAutoselectTargets} from './utils/collectChartAutoselectTargets';
import {openReportExcerptInNewTab} from '../collection-requests/coverage/navigateToReportExcerpt';
import {handleAiAssistantCopyEvent} from './utils/wysiwygCopy';
import {summarizeMarkupApplyResult} from '../whiteboards/ai/chartUserMessages';
import {applyEntityFilterActions} from '../whiteboards/utils/chartFacetChips';

const AIAssistantChatPanel = ({
    conversationId = null,
    documentId = null,
    /** Array of all selected document ids (multi-context). Falls back to [documentId]. */
    documentIds,
    documentType = 'report',
    documentTitle = '',
    /** Active chart grounding when on `/whiteboards/:id` — passed into agentic `context.chart`. */
    chartContext = null,
    emptyStateDocumentTitle = '',
    emptyStatePreset = 'report',
    apiBaseUrl,
    focusRequestToken = 0,
    // MINSKY-16657: the old raw-SSE-event debug view doesn't apply to the agentic backend's
    // event model — accepted (so `AIAssistantChatTab.jsx`'s prop contract stays unchanged) but
    // currently a no-op. `reasoningLog`/`steps` (always visible below) already surface the
    // agentic run's own progress narration.
    showDebug = false,
    isActive = true,
    title = 'Minsky Assistant',
    showHeader = true,
    autoSubmitRequest = null,
    onAutoSubmitHandled,
    onAction,
    onComposerFocusChange,
    onConversationChange,
    onError,
    /** Optional ref — set to the startNewConversation callback so parent can trigger it */
    startNewConversationRef,
}) => {
    const composerInputRef = React.useRef(null);
    const handledFocusRequestTokenRef = React.useRef(0);
    const composerFocusRetryTimeoutRef = React.useRef(null);
    const [composerText, setComposerText] = React.useState('');
    const [traceIndirectPickerOpen, setTraceIndirectPickerOpen] = React.useState(false);
    const bridgeChartEntities = useChartAiBridgeStore((s) => s.chartEntities);
    const bridgeChartConnections = useChartAiBridgeStore((s) => s.chartConnections);
    const bridgeChartId = useChartAiBridgeStore((s) => s.chartId);
    const canApplyMarkupForChart = useChartAiBridgeStore((s) => s.canApplyMarkup);
    const canApplyForChart = useChartAiBridgeStore((s) => s.canApply);

    // Inline chips: `citation` → FINTEL preview; `chart` → linked whiteboard; `kb` → entity drawer;
    // `entity` → EntityInfoDrawer via chart AI bridge. Uses `window.location.assign` for experts.
    const handleSelectComponent = React.useCallback(({type, payload} = {}) => {
        if (type === 'citation') {
            const reportId = payload?.reportId;
            if (!reportId) {
                return;
            }
            openReportExcerptInNewTab(reportId, payload?.excerpt || payload?.content || '');
            return;
        }
        if (type === 'chart') {
            const chartId = payload?.chartId;
            if (!chartId) {
                return;
            }
            window.open(`/charts/${encodeURIComponent(chartId)}`, '_blank', 'noopener,noreferrer');
            return;
        }
        if (type === 'kb') {
            const entityRef = resolveEntityRefFromPayload(payload);
            if (!entityRef) {
                return;
            }
            const {openEntity, canOpenEntity} = useChartAiBridgeStore.getState();
            if (canOpenEntity() && typeof openEntity === 'function') {
                openEntity(entityRef);
            }
            return;
        }
        if (type === 'entity') {
            const entityRef = resolveEntityRefFromPayload(payload);
            if (!entityRef) {
                return;
            }
            const {openEntity, canOpenEntity} = useChartAiBridgeStore.getState();
            if (canOpenEntity() && typeof openEntity === 'function') {
                openEntity(entityRef);
            }
            return;
        }
        if (type !== 'user' || !payload?.userId) {
            return;
        }
        getCachedExpertByUid(payload.userId)
            .then((expert) => {
                if (expert?.handle) {
                    window.location.assign(`/experts/${encodeURIComponent(expert.handle)}`);
                }
            })
            .catch((err) => {
                console.error('[AIAssistantChatPanel] Failed to resolve author page', err);
            });
    }, []);

    const agenticDocuments = React.useMemo(() => {
        if (chartContext?.chartId) {
            return [];
        }
        const ids = Array.isArray(documentIds) && documentIds.length > 0
            ? documentIds
            : (documentId ? [documentId] : []);
        return ids.filter(Boolean).map((id) => ({
            documentId: id,
            documentTitle: documentTitle || '',
        }));
    }, [chartContext?.chartId, documentId, documentIds, documentTitle]);

    const {
        status,
        goal,
        runId,
        userReaction = null,
        setUserReaction,
        steps,
        reasoningLog,
        finalAnswerText,
        streamingAnswerText,
        citations,
        error,
        history = [],
        pendingChartMutations,
        pendingChartMarkup,
        pendingChartMutationMode = 'confirm',
        pendingChartMutationFrozen = false,
        pendingChartMutationOpStatuses = null,
        pendingContextSuggestions = [],
        pureLlmEnabled = false,
        chartApplyLog,
        chartApplyGeneration = 0,
        conversationId: liveConversationId,
        loadingConversation,
        conversationError,
        submitGoal,
        startNewConversation,
        rejectChartMutations,
        freezeChartMutations,
        linkContextSuggestion,
        dismissContextSuggestion,
        dismissAllContextSuggestions,
        logChartApply,
        pendingEntityFilterActions,
    } = useAgenticRunController({
        apiBaseUrl,
        conversationId,
        onConversationChange,
        chartContext,
        documents: agenticDocuments,
    });

    const setEntityFilters = useChartAiBridgeStore((s) => s.setEntityFilters);
    const appliedEntityFilterActionsKeyRef = React.useRef(null);
    React.useEffect(() => {
        if (!Array.isArray(pendingEntityFilterActions) || pendingEntityFilterActions.length === 0) {
            return;
        }
        if (typeof setEntityFilters !== 'function') {
            return;
        }
        const applyKey = `${runId || ''}::${JSON.stringify(pendingEntityFilterActions)}`;
        if (appliedEntityFilterActionsKeyRef.current === applyKey) {
            return;
        }
        appliedEntityFilterActionsKeyRef.current = applyKey;
        setEntityFilters(applyEntityFilterActions([], pendingEntityFilterActions));
    }, [pendingEntityFilterActions, runId, setEntityFilters]);

    // Do NOT mark markup "already applied" before the bridge call finishes. Effect cleanup
    // (React Strict Mode, dep churn, bridge re-register) cancels the in-flight attempt; a
    // sticky pre-apply ref would skip the remounted effect and leave the chart unchanged while
    // the server narrator already claimed success.
    // After a finished attempt, remember the markup string so run-completed metadata restore
    // does not re-apply (and spuriously warn "no chart changes").
    // Always pass the markup into rejectChartMutations so a stale finally cannot clear a newer
    // follow-up proposal (first populate works; later edits narrate success but never apply).
    const bridgeApplyMarkupMutations = useChartAiBridgeStore((s) => s.applyMarkupMutations);
    const autoApplyIdRef = React.useRef(0);
    /** @type {React.MutableRefObject<{generation: number, markup: string|null}>} */
    const consumedApplyRef = React.useRef({generation: -1, markup: null});
    const [autoApplyingMarkup, setAutoApplyingMarkup] = React.useState(false);
    const [autoApplyError, setAutoApplyError] = React.useState(null);
    // Pure LLM tidy markup (move-entity only): apply immediately (whiteboard Undo is recovery).
    // Structural edits (add/remove entity/arrow) always show PureLlmMutationPreview — even when
    // the server resolves mutationMode=auto for a named add on a non-empty chart.
    const shouldAutoApplyPendingMarkup = Boolean(
        pendingChartMarkup
        && !pendingChartMutationFrozen
        && isMovesOnlyMarkup(pendingChartMarkup)
    );

    const isMarkupConsumedForGeneration = React.useCallback((markup, generation) => (
        consumedApplyRef.current.generation === generation
        && consumedApplyRef.current.markup === markup
    ), []);

    // New turn: allow the same markup string to apply again on a later `submitGoal`.
    React.useEffect(() => {
        consumedApplyRef.current = {generation: -1, markup: null};
        setAutoApplyError(null);
    }, [chartApplyGeneration]);

    React.useEffect(() => {
        if (!pureLlmEnabled || !shouldAutoApplyPendingMarkup) {
            return undefined;
        }
        if (isMarkupConsumedForGeneration(pendingChartMarkup, chartApplyGeneration)) {
            // Already applied this turn (e.g. run-completed metadata restore after success).
            rejectChartMutations(pendingChartMarkup);
            return undefined;
        }
        if (typeof bridgeApplyMarkupMutations !== 'function') {
            // Whiteboard applier not registered yet — retry when the bridge updates.
            return undefined;
        }

        const markup = pendingChartMarkup;
        const turnGeneration = chartApplyGeneration;
        const applyId = autoApplyIdRef.current + 1;
        autoApplyIdRef.current = applyId;
        setAutoApplyingMarkup(true);
        setAutoApplyError(null);
        let applySucceeded = false;

        void bridgeApplyMarkupMutations({markup, runHubSquare: true})
            .then((result) => {
                if (applyId !== autoApplyIdRef.current) {
                    return;
                }
                const summary = summarizeMarkupApplyResult(result);
                if (summary.ok) {
                    applySucceeded = true;
                    consumedApplyRef.current = {generation: turnGeneration, markup};
                }
                if (summary.message) {
                    logChartApply(summary.message);
                }
                if (!summary.ok) {
                    setAutoApplyError(summary.message);
                }
            })
            .catch((error) => {
                if (applyId !== autoApplyIdRef.current) {
                    return;
                }
                console.error('[AIAssistantChatPanel] Auto-apply chart markup failed', error);
                const message = error?.message || 'Failed to apply chart changes.';
                setAutoApplyError(message);
                logChartApply(message);
            })
            .finally(() => {
                if (applyId !== autoApplyIdRef.current) {
                    return;
                }
                setAutoApplyingMarkup(false);
                // Keep pending on failure so run-completed / bridge retry can re-attempt.
                if (applySucceeded) {
                    rejectChartMutations(markup);
                }
            });

        return () => {
            // Invalidate this attempt so remount can apply the same markup again.
            if (autoApplyIdRef.current === applyId) {
                autoApplyIdRef.current += 1;
            }
        };
    }, [
        bridgeApplyMarkupMutations,
        chartApplyGeneration,
        isMarkupConsumedForGeneration,
        logChartApply,
        pendingChartMarkup,
        pureLlmEnabled,
        rejectChartMutations,
        shouldAutoApplyPendingMarkup,
    ]);
    const showAutoApplySpinner = Boolean(pureLlmEnabled && autoApplyingMarkup);
    const previewChartId = chartContext?.chartId || bridgeChartId || undefined;
    const showMarkupPreview = Boolean(
        pureLlmEnabled && pendingChartMarkup && !shouldAutoApplyPendingMarkup
    );
    const markupPreviewEmpty = showMarkupPreview
        && !pendingChartMutationFrozen
        && isEmptyConfirmableMarkupPreview({
            markup: pendingChartMarkup,
            chartEntities: bridgeChartEntities,
            chartConnections: bridgeChartConnections,
            opStatuses: pendingChartMutationOpStatuses,
            inferFromChart: chartApplyGeneration <= 0
                && canApplyMarkupForChart(previewChartId)
                && ((bridgeChartEntities || []).length > 0 || (bridgeChartConnections || []).length > 0),
        });
    const showMutationsPreview = Boolean(
        !showMarkupPreview && pendingChartMutations?.mutations?.length > 0
    );
    const mutationsPreviewEmpty = Boolean(
        pendingChartMutations
        && !showMarkupPreview
        && !(pendingChartMutations.mutations?.length > 0)
    );
    const chartBridgeReady = showMarkupPreview
        ? canApplyMarkupForChart(previewChartId)
        : canApplyForChart(previewChartId);
    const showBridgeNotReadyBanner = Boolean(
        ((showMarkupPreview && !markupPreviewEmpty) || showMutationsPreview)
        && !chartBridgeReady
    );
    // Markup empty copy lives in PureLlmMutationPreview — a second banner here
    // doubled "No applicable chart changes" after filters caught up with Apply.
    const showEmptyPreviewStatus = mutationsPreviewEmpty;

    const handleStartNewConversation = React.useCallback(() => {
        setTraceIndirectPickerOpen(false);
        startNewConversation();
    }, [startNewConversation]);

    const prevRunStatusRef = React.useRef(status);
    React.useEffect(() => {
        const prevStatus = prevRunStatusRef.current;
        prevRunStatusRef.current = status;
        const justCompleted = status === AGENTIC_RUN_STATUS.COMPLETED
            && (prevStatus === AGENTIC_RUN_STATUS.RUNNING || prevStatus === AGENTIC_RUN_STATUS.STARTING);
        if (!justCompleted || !chartContext?.chartId) {
            return;
        }
        const {canSelectShapes, selectShapes} = useChartAiBridgeStore.getState();
        if (!canSelectShapes() || typeof selectShapes !== 'function') {
            return;
        }
        const targets = collectChartAutoselectTargets({
            answerText: finalAnswerText,
            mutations: pendingChartMutations?.mutations,
            chartConnections: bridgeChartConnections,
        });
        if (targets.entityRefs.length === 0 && targets.arrowShapeIds.length === 0) {
            return;
        }
        selectShapes(targets);
    }, [
        status,
        chartContext?.chartId,
        finalAnswerText,
        pendingChartMutations,
        bridgeChartConnections,
    ]);

    React.useEffect(() => {
        if (error) {
            onError?.(new Error(error));
        }
    }, [error, onError]);

    React.useEffect(() => {
        if (!startNewConversationRef) return undefined;
        startNewConversationRef.current = handleStartNewConversation;
        return () => { startNewConversationRef.current = null; };
    }, [startNewConversationRef, handleStartNewConversation]);

    const isBusy = status === AGENTIC_RUN_STATUS.STARTING || status === AGENTIC_RUN_STATUS.RUNNING;
    const displayAnswerText = status === AGENTIC_RUN_STATUS.COMPLETED
        ? finalAnswerText
        : streamingAnswerText;
    const showStreamingSpinner = isBusy && !displayAnswerText;
    const composerDisabled = !isActive;
    const submitDisabled = !isActive || isBusy;
    const hasQueuedAutoSubmit = Boolean(
        isActive
        && autoSubmitRequest?.id
        && typeof autoSubmitRequest?.text === 'string'
        && autoSubmitRequest.text.trim().length > 0
    );
    const hasActiveTurn = Boolean(goal) || isBusy;
    const hasAnyExchange = hasActiveTurn || history.length > 0;
    const shouldShowEmptyState = !loadingConversation
        && !hasAnyExchange
        && !hasQueuedAutoSubmit
        && !traceIndirectPickerOpen;
    const composerPlaceholder = chartContext?.chartId
        ? 'Ask about this chart…'
        : 'Ask about this document…';

    const {scrollerRef, lastItemRef, handleScroll} = useAgenticScrollToBottom(buildAgenticScrollMetrics({
        conversationId: liveConversationId,
        historyLength: history.length,
        hasActiveTurn,
        goal,
        lastHistoryId: history[history.length - 1]?.id || null,
        pickerOpen: traceIndirectPickerOpen && !isBusy,
    }));

    const handleTranscriptCopyCapture = React.useCallback((event) => {
        handleAiAssistantCopyEvent(event, scrollerRef.current, {
            citations,
            stopPropagation: true,
        });
    }, [citations]);

    const focusComposer = React.useCallback(() => {
        const composerHandle = composerInputRef.current;
        if (!composerHandle || composerDisabled) {
            return false;
        }

        if (typeof composerHandle.focus === 'function') {
            composerHandle.focus();
        }

        if (typeof composerHandle.hasFocus === 'function') {
            return composerHandle.hasFocus();
        }

        return document.activeElement === composerHandle;
    }, [composerDisabled]);

    React.useEffect(() => {
        if (!isActive) {
            onComposerFocusChange?.(false);
            return undefined;
        }

        if (!focusRequestToken
            || handledFocusRequestTokenRef.current === focusRequestToken
            || composerDisabled) {
            return undefined;
        }

        let cancelled = false;

        const clearPendingRetry = () => {
            if (composerFocusRetryTimeoutRef.current) {
                window.clearTimeout(composerFocusRetryTimeoutRef.current);
                composerFocusRetryTimeoutRef.current = null;
            }
        };

        const attemptFocus = (remainingAttempts = 5) => {
            if (cancelled) {
                return;
            }

            if (focusComposer()) {
                handledFocusRequestTokenRef.current = focusRequestToken;
                clearPendingRetry();
                return;
            }

            if (remainingAttempts <= 0) {
                return;
            }

            composerFocusRetryTimeoutRef.current = window.setTimeout(() => {
                attemptFocus(remainingAttempts - 1);
            }, 30);
        };

        attemptFocus();

        return () => {
            cancelled = true;
            clearPendingRetry();
        };
    }, [composerDisabled, focusComposer, focusRequestToken, isActive, onComposerFocusChange]);

    React.useEffect(() => () => {
        if (composerFocusRetryTimeoutRef.current) {
            window.clearTimeout(composerFocusRetryTimeoutRef.current);
        }
        onComposerFocusChange?.(false);
    }, [onComposerFocusChange]);

    const handleSubmit = React.useCallback((text) => {
        const trimmed = (text || '').trim();
        if (!trimmed || submitDisabled) {
            return;
        }
        if (isTraceIndirectConnectionsIntent(trimmed)) {
            setTraceIndirectPickerOpen(true);
            setComposerText('');
            return;
        }
        setTraceIndirectPickerOpen(false);
        submitGoal(trimmed);
        setComposerText('');
    }, [submitDisabled, submitGoal]);

    React.useEffect(() => {
        const requestId = autoSubmitRequest?.id;
        const requestText = autoSubmitRequest?.text;

        if (!isActive || !requestId || !requestText || isBusy) {
            return;
        }

        handleSubmit(requestText);
        onAutoSubmitHandled?.(requestId);
    }, [autoSubmitRequest, handleSubmit, isActive, isBusy, onAutoSubmitHandled]);

    const handleAction = React.useCallback((action) => {
        if (!action || typeof action !== 'object') {
            return;
        }

        if (action.type === 'tab') {
            onAction?.(action);
            return;
        }

        if (action.type === 'trace_indirect_connections') {
            if (submitDisabled) {
                return;
            }
            setTraceIndirectPickerOpen(true);
            return;
        }

        if (action.type !== 'prompt') {
            return;
        }

        const normalizedPrompt = typeof action.prompt === 'string' ? action.prompt.trim() : '';
        if (!normalizedPrompt || submitDisabled) {
            return;
        }

        handleSubmit(normalizedPrompt);
    }, [handleSubmit, onAction, submitDisabled]);

    const handleTraceIndirectConfirm = React.useCallback((prompt) => {
        setTraceIndirectPickerOpen(false);
        handleSubmit(prompt);
    }, [handleSubmit]);

    const traceIndirectChartEntities = React.useMemo(() => {
        if (!chartContext?.chartId || chartContext.chartId !== bridgeChartId) {
            return [];
        }
        return bridgeChartEntities;
    }, [bridgeChartEntities, bridgeChartId, chartContext?.chartId]);

    const traceIndirectChartConnections = React.useMemo(() => {
        if (!chartContext?.chartId || chartContext.chartId !== bridgeChartId) {
            return [];
        }
        return bridgeChartConnections;
    }, [bridgeChartConnections, bridgeChartId, chartContext?.chartId]);

    // After submit the composer keeps focus (keepFocusOnSubmit). A mousedown on a panel
    // action would blur ProseMirror, collapse the Send footer, and reflow this flex column
    // mid-click — so the first press can miss Skip/Add and phrase chips. Same pattern as
    // CommentEditor's Send/Cancel buttons: suppress focus transfer on panel actions.
    const handlePanelActionMouseDownCapture = React.useCallback((event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        if (target.closest('.comment-editor-wrapper')) {
            return;
        }
        const actionEl = target.closest(
            'button:not([disabled]), [role="button"]:not([aria-disabled="true"]), .MuiChip-root:not(.Mui-disabled)'
        );
        if (actionEl) {
            event.preventDefault();
        }
    }, []);

    return (
        <Stack
            data-testid="ai-assistant-chat-panel"
            flex={1}
            minHeight={0}
            height="100%"
            bgcolor="background.paper"
            overflow="hidden"
            onCopyCapture={handleTranscriptCopyCapture}
            onMouseDownCapture={handlePanelActionMouseDownCapture}
            sx={{
                // Conversation reading text is 25% larger than the shared composer default.
                '& .comment-editor-content .ProseMirror': {
                    fontSize: 'calc(0.875rem * 1.25)',
                },
            }}
        >
            {showHeader && (
                <ChatHeader
                    title={title}
                    documentId={documentId}
                    documentTitle={documentTitle}
                    conversationId={conversationId}
                    onStartNewConversation={handleStartNewConversation}
                />
            )}

            {conversationError ? (
                <Stack px={2} pt={1}>
                    <Alert severity="error">{conversationError}</Alert>
                </Stack>
            ) : null}

            <Box
                ref={scrollerRef}
                data-testid="agentic-transcript-scroller"
                onScroll={handleScroll}
                sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5, userSelect: 'text'}}
            >
                {loadingConversation ? (
                    <Stack alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress size={28}/>
                    </Stack>
                ) : null}

                {shouldShowEmptyState ? (
                    <EmptyState
                        documentTitle={emptyStateDocumentTitle || documentTitle}
                        preset={emptyStatePreset}
                        onAction={handleAction}
                        promptActionDisabled={submitDisabled}
                    />
                ) : null}

                {!loadingConversation && (hasAnyExchange || traceIndirectPickerOpen) ? (
                    <Stack spacing={1.5}>
                        <AgenticExchangeHistory
                            exchanges={history}
                            chartId={chartContext?.chartId || bridgeChartId || undefined}
                            onSelectComponent={handleSelectComponent}
                            apiBaseUrl={apiBaseUrl}
                        />

                        {hasActiveTurn ? (
                            <Stack ref={lastItemRef} spacing={1.5}>
                                {goal ? (
                                    <Paper elevation={0} sx={{py: 1}}>
                                        <Box sx={{fontWeight: 600, fontSize: 14 * 1.25}}>{goal}</Box>
                                    </Paper>
                                ) : null}

                                <ReasoningSection entries={reasoningLog}/>
                                {isBusy ? (
                                    <AgenticStepList steps={steps}/>
                                ) : null}
                                {chartApplyLog.length > 0 ? (
                                    <AgenticStepList steps={chartApplyLog.map((entry) => ({
                                        id: entry.id,
                                        tool: 'chartApply',
                                        displayLabel: entry.message,
                                        status: entry.message?.match(/could not apply|failed/i)
                                            ? 'failed'
                                            : 'succeeded',
                                    }))}/>
                                ) : null}

                                {showStreamingSpinner ? (
                                    <Stack direction="row" spacing={1} alignItems="center" aria-label="Streaming response">
                                        <CircularProgress size={14}/>
                                    </Stack>
                                ) : null}

                                {status === AGENTIC_RUN_STATUS.FAILED && error ? (
                                    <Alert severity="error">{error}</Alert>
                                ) : null}

                                {displayAnswerText && (isBusy || status === AGENTIC_RUN_STATUS.COMPLETED) ? (
                                    <AgenticFinalAnswer
                                        text={displayAnswerText}
                                        citations={citations}
                                        onSelectComponent={handleSelectComponent}
                                        runId={status === AGENTIC_RUN_STATUS.COMPLETED ? runId : null}
                                        initialRating={userReaction}
                                        onRatingChange={setUserReaction}
                                        apiBaseUrl={apiBaseUrl}
                                    />
                                ) : null}

                                {showBridgeNotReadyBanner ? (
                                    <Alert
                                        severity="info"
                                        data-testid="chart-bridge-not-ready-banner"
                                    >
                                        {CHART_BRIDGE_NOT_READY_COPY}
                                    </Alert>
                                ) : null}

                                {showEmptyPreviewStatus ? (
                                    <Alert
                                        severity="info"
                                        data-testid="chart-preview-empty-status"
                                    >
                                        {CHART_PREVIEW_EMPTY_COPY}
                                    </Alert>
                                ) : null}

                                {showAutoApplySpinner ? (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        aria-label="Applying chart changes"
                                    >
                                        <CircularProgress size={14}/>
                                        <Typography variant="body2" color="text.secondary">
                                            Applying chart changes…
                                        </Typography>
                                    </Stack>
                                ) : showMarkupPreview ? (
                                    <PureLlmMutationPreview
                                        markup={pendingChartMarkup}
                                        chartId={previewChartId}
                                        frozen={pendingChartMutationFrozen}
                                        initialOpStatuses={pendingChartMutationOpStatuses || undefined}
                                        chartApplyGeneration={chartApplyGeneration}
                                        onReject={rejectChartMutations}
                                        onFrozen={freezeChartMutations}
                                    />
                                ) : showMutationsPreview ? (
                                    <ChartMutationPreview
                                        plan={pendingChartMutations}
                                        onReject={rejectChartMutations}
                                        onWorkLog={logChartApply}
                                    />
                                ) : null}

                                {autoApplyError ? (
                                    <Alert
                                        severity="warning"
                                        onClose={() => setAutoApplyError(null)}
                                    >
                                        {autoApplyError}
                                    </Alert>
                                ) : null}

                                {pureLlmEnabled && pendingContextSuggestions?.length > 0 ? (
                                    <ContextSuggestPreview
                                        suggestions={pendingContextSuggestions}
                                        onLink={linkContextSuggestion}
                                        onDismiss={dismissContextSuggestion}
                                        onDismissAll={dismissAllContextSuggestions}
                                    />
                                ) : null}
                            </Stack>
                        ) : null}

                        {traceIndirectPickerOpen && !isBusy ? (
                            <Stack ref={lastItemRef} spacing={1.5}>
                                <TraceIndirectConnectionsPicker
                                    chartEntities={traceIndirectChartEntities}
                                    chartConnections={traceIndirectChartConnections}
                                    onCancel={() => setTraceIndirectPickerOpen(false)}
                                    onConfirm={handleTraceIndirectConfirm}
                                />
                            </Stack>
                        ) : null}
                    </Stack>
                ) : null}
            </Box>

            {chartContext?.chartId && !shouldShowEmptyState ? (
                <ChartPhraseChips
                    onAction={handleAction}
                    disabled={submitDisabled}
                />
            ) : null}

            <MessageComposer
                inputRef={composerInputRef}
                value={composerText}
                onChange={setComposerText}
                onSubmit={handleSubmit}
                disabled={composerDisabled}
                submitDisabled={submitDisabled}
                onFocusChange={onComposerFocusChange}
                placeholder={composerPlaceholder}
                ariaLabel="AI assistant chat input"
            />
        </Stack>
    );
};

export default AIAssistantChatPanel;
